import BaseProduct from './BaseProduct';
import { ProductId } from '../types';
import store, { getLayerOpacity } from '../state';
import { getTileUrl } from '../config';
import { WildflowerService } from '../services/WildflowerService';

export class WildflowerLayer extends BaseProduct {
  public readonly id: ProductId;
  private unsubscribeStore: (() => void) | null = null;
  private lastDate: string = '';
  private lastViewMode: string = '';
  private lastDomain: string = '';
  private lastSpecies: string = '';

  constructor(map: any, layerManager: any, id: ProductId = 'alpine_wildflowers') {
    super(map, layerManager);
    this.id = id;
  }

  private getProductState(s: any): { dateStr: string; viewMode: string; domain: string; species: string } {
    const defaultInfo = WildflowerService.getDefaultDateInfo(this.id);
    if (this.id === 'desert_wildflowers') {
      return {
        dateStr: s.desertWildflowerSelectedDate || defaultInfo.dateStr,
        viewMode: s.desertWildflowerViewMode || 'peak_date',
        domain: s.desertWildflowerDomain || 'na',
        species: s.desertWildflowerSpecies || 'composite'
      };
    } else if (this.id === 'wild_berries') {
      return {
        dateStr: s.wildBerrySelectedDate || defaultInfo.dateStr,
        viewMode: s.wildBerryViewMode || 'peak_date',
        domain: s.wildBerryDomain || 'na',
        species: s.wildBerrySpecies || 'composite'
      };
    } else {
      // alpine_wildflowers
      return {
        dateStr: s.alpineWildflowerSelectedDate || s.wildflowerSelectedDate || defaultInfo.dateStr,
        viewMode: s.alpineWildflowerViewMode || s.wildflowerViewMode || 'peak_date',
        domain: s.alpineWildflowerDomain || s.wildflowerDomain || 'na',
        species: s.alpineWildflowerSpecies || s.wildflowerSpecies || 'composite'
      };
    }
  }

  protected onActivate(): void {
    this.refresh();
    this.unsubscribeStore = store.subscribe((s) => {
      if (!this.active) return;
      const { dateStr, viewMode, domain, species } = this.getProductState(s);

      if (
        dateStr !== this.lastDate ||
        viewMode !== this.lastViewMode ||
        domain !== this.lastDomain ||
        species !== this.lastSpecies
      ) {
        this.refresh();
      }
    });
  }

  protected onDeactivate(): void {
    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }
    this.layerManager.removeBufferedRasterLayer(this.id);
    this.layerManager.removeLayer(this.id);
    this.lastDate = '';
    this.lastViewMode = '';
    this.lastDomain = '';
    this.lastSpecies = '';
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    this.refresh();
  }

  public refresh(): void {
    if (!this.active) return;
    const state = store.getState();
    const manifest = state.manifest;
    if (!manifest) return;

    const opacity = getLayerOpacity(this.id, 1.0);
    const { dateStr, viewMode, domain, species } = this.getProductState(state);

    this.lastDate = dateStr;
    this.lastViewMode = viewMode;
    this.lastDomain = domain;
    this.lastSpecies = species;

    const cleanDate = dateStr.replace(/-/g, '');
    let target = '';
    let effMode: string = viewMode;
    if (this.id === 'alpine_wildflowers') {
      target = viewMode === 'peak_date' ? (domain === 'global' ? 'composite' : species) : cleanDate;
    } else if (this.id === 'desert_wildflowers') {
      effMode = viewMode === 'mbii' ? 'bii' : (viewMode === 'status' ? 'stage' : 'peak_date');
      target = effMode === 'peak_date' ? '' : cleanDate;
    } else if (this.id === 'wild_berries') {
      effMode = viewMode === 'mbii' ? 'quality' : (viewMode === 'status' ? 'stage' : 'peak_date');
      target = effMode === 'peak_date' ? (domain === 'global' ? 'composite' : (species || 'composite')) : cleanDate;
    }
    const tileUrl = getTileUrl(manifest, this.id, effMode, domain, target);
    this.layerManager.updateBufferedRasterLayer(this.id, [tileUrl], opacity, undefined, 0, 6, 'nearest');
  }
}

export default WildflowerLayer;
